import { useFormContext } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import FormSection from '../../form/FormSection';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { LegacyIcon } from '../../icon';
import DataTable from '../../data-table/DataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import {
    AGENT_MODEL_SHORT_DISPLAY_FIELD,
    AGENT_NAME_FIELD,
    AGENT_NETWORK_HOSTNAME_FIELD,
    AGENT_SOFTWARE_VERSION_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_TAGS_DISPLAY_FIELD,
    AGENT_TRANSLATED_TYPE_FIELD,
    AGENT_TYPE_ICON_FIELD,
    AGENT_WIRED_FIELD,
} from '../agents/AgentConstants';
import { renderStatus, renderVersion } from '../agents/AgentRenderHelper';
import { useColumnStyles } from '../agents/AgentStyles';
import { AGENTS_FIELD, IS_DEFAULT_FIELD } from './WiredNetworksConstants';
import { ID_FIELD } from '../../form/FormConstants';
import ListSelectorField from '../../form/ListSelectorField';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { softwareVersionMapper } from '../agents/AgentVersionHelper';

const initialSort = [{ property: AGENT_NAME_FIELD, direction: SORT_ASC }];

function buildAgentColumns(classes) {
    return [
        {
            type: COLUMN_TYPES.ICON,
            title: t`Type`,
            width: 74,
            mapping: AGENT_TYPE_ICON_FIELD,
            sortable: true,
            csvFormatter: (value, row) => row[AGENT_TRANSLATED_TYPE_FIELD],
            icons: [
                {
                    getIcon: (row) => <LegacyIcon icon={row[AGENT_TYPE_ICON_FIELD]} />,
                    getTooltip: (row) => row[AGENT_TRANSLATED_TYPE_FIELD],
                },
            ],
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Name`,
            removable: false,
            sortable: true,
            mapping: AGENT_NAME_FIELD,
            width: 250,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            sortable: true,
            mapping: AGENT_STATUS_FIELD,
            width: 105,
            className: classes.root,
            renderer: renderStatus,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Tags`,
            sortable: true,
            mapping: AGENT_TAGS_DISPLAY_FIELD,
            width: 200,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Version`,
            sortable: true,
            mapping: AGENT_SOFTWARE_VERSION_FIELD,
            className: classes.root,
            mapper: softwareVersionMapper,
            renderer: renderVersion,
            width: 125,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Hostname`,
            sortable: true,
            mapping: AGENT_NETWORK_HOSTNAME_FIELD,
            width: 175,
        },
    ];
}

const defaultDataSource = {
    url: '/query/admin/agents',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        qWiredConfigured: 'DEFAULT',
        searchFields: [
            AGENT_NAME_FIELD,
            AGENT_STATUS_FIELD,
            AGENT_TAGS_DISPLAY_FIELD,
            AGENT_MODEL_SHORT_DISPLAY_FIELD,
            AGENT_WIRED_FIELD,
            AGENT_NETWORK_HOSTNAME_FIELD,
        ],
    },
};

const selectionDataSource = {
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        nextGen: true,
        searchFields: [
            AGENT_NAME_FIELD,
            AGENT_STATUS_FIELD,
            AGENT_TAGS_DISPLAY_FIELD,
            AGENT_MODEL_SHORT_DISPLAY_FIELD,
            AGENT_WIRED_FIELD,
            AGENT_NETWORK_HOSTNAME_FIELD,
        ],
    },
    initialSort: [{ property: AGENT_NAME_FIELD, direction: SORT_ASC }],
};

const selectedDataSource = {
    ...selectionDataSource,
    url: '/query/admin/agents/findSelected',
};

const availableDataSource = {
    ...selectionDataSource,
    url: '/query/admin/agents/findAvailable',
};

const idsDataSource = {
    params: selectionDataSource.params,
    url: '/query/admin/agents/selectionUUIDs',
};

function getDefaultDataSource(wiredNetworkId) {
    _.set(defaultDataSource, 'params.wiredNetworkId', wiredNetworkId);
    return defaultDataSource;
}

function WiredNetworksAgents({ formData, setAgentSelectionIsDirty }) {
    const classes = useColumnStyles();
    const { watch } = useFormContext();
    const loader = useQueryLoader(getDefaultDataSource(formData[ID_FIELD]), { disableFilterState: true });
    const gridColumns = buildAgentColumns(classes);
    const isDefaultGrid = watch(IS_DEFAULT_FIELD, formData[IS_DEFAULT_FIELD]);

    return (
        <FormSection title={<Trans>Assigned nPoints</Trans>} addSpacer>
            {isDefaultGrid ? (
                <DataTable tableId="Wired Network Agents" loader={loader} columns={gridColumns} sorters={initialSort} />
            ) : (
                <ListSelectorField
                    gridColumns={gridColumns}
                    sorters={initialSort}
                    dbSelected={formData[AGENTS_FIELD]}
                    selectedDataSource={selectedDataSource}
                    availableDataSource={availableDataSource}
                    idsDataSource={idsDataSource}
                    fieldName={AGENTS_FIELD}
                    addButtonText={<Trans>Add nPoints</Trans>}
                    removeButtonText={<Trans>Remove nPoints</Trans>}
                    setSelectionIsDirty={setAgentSelectionIsDirty}
                />
            )}
        </FormSection>
    );
}

export default WiredNetworksAgents;
